/**
 * Pure state for the Live console: the per-capability consent ladder, control
 * grants, chat, control results, and helpers that translate agent gestures
 * into protocol messages. Framework-free and unit-tested.
 */
import {
  CONSENT_WORDING,
  CONTROL_POLICY,
  type AgentIdentity,
  type AnnotationShape,
  type ConsentState,
  type ControlCommand,
  type LiveCapability,
  type UserToAgent,
} from "@backstory/protocol";

export const CAPABILITIES: LiveCapability[] = ["cobrowse", "voice", "annotate", "control", "record"];

/**
 * The agent makes at most two requests: support access (`cobrowse` on the
 * backwards-compatible wire contract) grants chat, annotation, and remote
 * control together; voice remains a separate grant.
 */
// Keep record in the wire/storage types for backwards compatibility, but do
// not expose it as a requestable product capability yet.
export const REQUESTABLE: LiveCapability[] = ["cobrowse", "voice"];

export const CAPABILITY_LABEL: Record<LiveCapability, string> = {
  cobrowse: "Support access",
  voice: "Voice call",
  annotate: "Annotate",
  control: "Remote control",
  record: "Record session",
};

export const CAPABILITY_HINT: Record<LiveCapability, string> = {
  cobrowse: "Chat, annotate, and remotely control the page under one user-approved support grant. Sensitive data stays masked.",
  voice: "Audio-only call over LiveKit. Not recorded unless the user agrees separately.",
  annotate: "Draw, point, and highlight elements. Drawings fade after 10 seconds.",
  control: "Click and type for the user until either side stops support access.",
  record: "Store this assist (screen view, drawings, audio) as a session.",
};

export type RungState = "idle" | "requested" | "granted" | "declined" | "revoked" | "expired";

export interface ChatLine {
  from: "agent" | "user";
  text: string;
  at: number;
}

export interface ControlResultLine {
  commandId: string;
  ok: boolean;
  error?: NonNullable<Extract<UserToAgent, { type: "control_result" }>["error"]>;
  command?: ControlCommand;
  at: number;
}

export interface LiveState {
  rungs: Record<LiveCapability, RungState>;
  /** requestId -> capability, for pending requests. */
  requestIds: Record<string, LiveCapability>;
  consent?: ConsentState;
  url?: string;
  viewport?: { w: number; h: number };
  controlExpiresAt?: number;
  chat: ChatLine[];
  controlResults: ControlResultLine[];
  pendingCommands: Record<string, ControlCommand>;
  controlActions: number;
  voiceStatus?: { connected: boolean; error?: string };
  helpCode?: string;
  /** The user moved or typed; we are paused until `userActiveUntil`. */
  userActiveUntil?: number;
  /** Whether the recorded page still holds a socket, per the relay. */
  userOnline?: boolean;
  ended?: { reason: string; at: number };
}

export function initialState(): LiveState {
  return {
    rungs: { cobrowse: "idle", voice: "idle", annotate: "idle", control: "idle", record: "idle" },
    requestIds: {},
    chat: [],
    controlResults: [],
    pendingCommands: {},
    controlActions: 0,
  };
}

/** Wording the end user sees for a request, with agent/org substituted. */
export function wordingFor(capability: LiveCapability, agent: AgentIdentity): string {
  return CONSENT_WORDING[capability].replaceAll("{agent}", agent.name).replaceAll("{org}", agent.org);
}

export function markRequested(state: LiveState, capability: LiveCapability, requestId: string): LiveState {
  return {
    ...state,
    rungs: { ...state.rungs, [capability]: "requested" },
    requestIds: { ...state.requestIds, [requestId]: capability },
    voiceStatus: capability === "voice" ? undefined : state.voiceStatus,
  };
}

export function markRevokedByAgent(state: LiveState, capability: LiveCapability): LiveState {
  if (capability === "cobrowse") {
    const consent = state.consent ? { ...state.consent, cobrowse: false, annotate: false, control: false, controlExpiresAt: undefined } : state.consent;
    return { ...state, rungs: { ...state.rungs, cobrowse: "revoked", annotate: "revoked", control: "revoked" }, consent, controlExpiresAt: undefined };
  }
  const consent = state.consent ? { ...state.consent, [capability]: false } : state.consent;
  return { ...state, rungs: { ...state.rungs, [capability]: "revoked" }, consent, controlExpiresAt: capability === "control" ? undefined : state.controlExpiresAt };
}

export function addAgentChat(state: LiveState, text: string, now = Date.now()): LiveState {
  return { ...state, chat: [...state.chat, { from: "agent", text, at: now }] };
}

export function addPendingCommand(state: LiveState, commandId: string, command: ControlCommand): LiveState {
  return { ...state, pendingCommands: { ...state.pendingCommands, [commandId]: command } };
}

/** Apply a message from the end user's SDK. */
export function reduce(state: LiveState, msg: UserToAgent, now = Date.now()): LiveState {
  switch (msg.type) {
    case "response": {
      const cap = state.requestIds[msg.requestId] ?? msg.capability;
      const requestIds = { ...state.requestIds };
      delete requestIds[msg.requestId];
      const rung: RungState = msg.granted ? "granted" : "declined";
      const consent = state.consent ? { ...state.consent, [cap]: msg.granted } : state.consent;
      const bundled = cap === "cobrowse" && msg.granted;
      const rungs = bundled
        ? { ...state.rungs, cobrowse: rung, annotate: "granted" as const, control: "granted" as const }
        : { ...state.rungs, [cap]: rung };
      const nextConsent = bundled && consent ? { ...consent, cobrowse: true, annotate: true, control: true } : consent;
      return { ...state, requestIds, rungs, consent: nextConsent, controlExpiresAt: undefined };
    }
    case "revoked": {
      const rung: RungState = msg.by === "expiry" ? "expired" : "revoked";
      const consent = state.consent ? { ...state.consent, [msg.capability]: false } : state.consent;
      return { ...state, rungs: { ...state.rungs, [msg.capability]: rung }, consent, controlExpiresAt: msg.capability === "control" ? undefined : state.controlExpiresAt };
    }
    case "state": {
      const rungs = { ...state.rungs };
      for (const cap of CAPABILITIES) {
        if (msg.consent[cap] && rungs[cap] !== "granted") rungs[cap] = "granted";
        if (!msg.consent[cap] && rungs[cap] === "granted") rungs[cap] = "revoked";
      }
      return { ...state, rungs, consent: msg.consent, url: msg.url, viewport: msg.viewport, controlExpiresAt: undefined };
    }
    case "voice_status":
      return { ...state, voiceStatus: { connected: msg.connected, error: msg.error } };
    case "control_result": {
      const command = state.pendingCommands[msg.commandId];
      const pendingCommands = { ...state.pendingCommands };
      delete pendingCommands[msg.commandId];
      const line: ControlResultLine = { commandId: msg.commandId, ok: msg.ok, error: msg.error, command, at: now };
      return {
        ...state,
        pendingCommands,
        controlResults: [...state.controlResults.slice(-49), line],
        controlActions: state.controlActions + (msg.ok ? 1 : 0),
        userActiveUntil: msg.error === "user_priority" ? now + CONTROL_POLICY.userPriorityResumeMs : state.userActiveUntil,
      };
    }
    case "help_request":
      return { ...state, helpCode: msg.code, chat: msg.message ? [...state.chat, { from: "user", text: msg.message, at: now }] : state.chat };
    case "chat":
      return { ...state, chat: [...state.chat, { from: "user", text: msg.text, at: now }] };
    case "presence":
      return { ...state, userOnline: msg.online };
    case "ended":
      return { ...state, ended: { reason: msg.reason, at: now }, rungs: { cobrowse: "revoked", voice: "revoked", annotate: "revoked", control: "revoked", record: "revoked" }, controlExpiresAt: undefined };
    default:
      return state;
  }
}

export function isGranted(state: LiveState, cap: LiveCapability): boolean {
  return state.rungs[cap] === "granted";
}

// ---------------------------------------------------------------------------
// Gesture → protocol helpers
// ---------------------------------------------------------------------------

export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Normalize a client point into 0..1 coordinates relative to the live stage. */
export function normalizePoint(clientX: number, clientY: number, rect: Rect): { x: number; y: number } {
  const w = rect.width || 1;
  const h = rect.height || 1;
  const x = Math.min(1, Math.max(0, (clientX - rect.left) / w));
  const y = Math.min(1, Math.max(0, (clientY - rect.top) / h));
  return { x: round4(x), y: round4(y) };
}

const round4 = (n: number) => Math.round(n * 10_000) / 10_000;

let idCounter = 0;
export function newShapeId(prefix = "a"): string {
  idCounter += 1;
  return `${prefix}_${Date.now().toString(36)}_${idCounter}`;
}

export const ANNOTATION_TTL_MS = 10_000;
export const DEFAULT_ANNOTATION_COLOR = "#f97316";

export function penShape(points: Array<{ x: number; y: number }>, color = DEFAULT_ANNOTATION_COLOR, width = 3): AnnotationShape {
  return { kind: "pen", id: newShapeId("pen"), points, color, width, ttlMs: ANNOTATION_TTL_MS };
}
export function arrowShape(from: { x: number; y: number }, to: { x: number; y: number }, color = DEFAULT_ANNOTATION_COLOR): AnnotationShape {
  return { kind: "arrow", id: newShapeId("arrow"), from, to, color, ttlMs: ANNOTATION_TTL_MS };
}
export function rectShape(a: { x: number; y: number }, b: { x: number; y: number }, color = DEFAULT_ANNOTATION_COLOR): AnnotationShape {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { kind: "rect", id: newShapeId("rect"), x, y, w: round4(Math.abs(b.x - a.x)), h: round4(Math.abs(b.y - a.y)), color, ttlMs: ANNOTATION_TTL_MS };
}
export function highlightShape(nodeId: number, color = DEFAULT_ANNOTATION_COLOR): AnnotationShape {
  return { kind: "highlight", id: newShapeId("hl"), nodeId, color, ttlMs: ANNOTATION_TTL_MS };
}
export function laserShape(p: { x: number; y: number }): AnnotationShape {
  return { kind: "laser", id: "laser", x: p.x, y: p.y };
}
export function textShape(p: { x: number; y: number }, text: string, color = DEFAULT_ANNOTATION_COLOR): AnnotationShape {
  return { kind: "text", id: newShapeId("txt"), x: p.x, y: p.y, text, color, ttlMs: ANNOTATION_TTL_MS };
}

/** A click on a mirrored node becomes a control command with normalized coordinates. */
export function clickCommand(nodeId: number, point?: { x: number; y: number }): ControlCommand {
  return point ? { kind: "click", nodeId, x: point.x, y: point.y } : { kind: "click", nodeId };
}
export function inputCommand(nodeId: number, value: string): ControlCommand {
  return { kind: "input", nodeId, value };
}
export function keyCommand(key: string, nodeId?: number): ControlCommand {
  return nodeId != null ? { kind: "key", key, nodeId } : { kind: "key", key };
}
/** Wheel input: the mirror cannot know the user's live offset, so send the delta. */
export function scrollByCommand(nodeId: number, top: number, left = 0): ControlCommand {
  return { kind: "scroll", nodeId, top, left, by: true };
}

/** Scrollbar input: its thumb represents an absolute position in the viewport. */
export function scrollCommand(nodeId: number, top: number, left = 0): ControlCommand {
  return { kind: "scroll", nodeId, top, left };
}

/** Client-side mirror of CONTROL_POLICY so the console can warn before sending. */
export function policyBlocks(el: Element | null): string | null {
  if (!el) return null;
  const tag = el.tagName.toLowerCase();
  const type = (el.getAttribute("type") ?? "").toLowerCase();
  if (tag === "input" && (CONTROL_POLICY.blockedInputTypes as readonly string[]).includes(type)) return "password fields are never controlled";
  const ac = (el.getAttribute("autocomplete") ?? "").toLowerCase();
  if (ac && (CONTROL_POLICY.blockedAutocomplete as readonly string[]).includes(ac)) return "payment and one-time-code fields are never controlled";
  for (const sel of CONTROL_POLICY.blockedSelectors) if (el.closest(sel)) return "masked or blocked content is never controlled";
  if (tag === "input" && type === "file") return "file pickers cannot be driven remotely; guide the user instead";
  return null;
}

export const CONTROL_ERROR_TEXT: Record<NonNullable<ControlResultLine["error"]>, string> = {
  blocked_by_policy: "Blocked by policy: sensitive fields and masked content cannot be controlled.",
  not_synthesizable: "The browser will not let this action be triggered remotely. Ask the user to do it — the element is highlighted for them.",
  target_missing: "That element is no longer on the page.",
  user_priority: "The user is active; control pauses for 2 seconds.",
};
